import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MailCheck, AlertCircle, Calendar, Inbox,
  Search, RefreshCw, Eye, X, ExternalLink,
  ChevronLeft, ChevronRight, ArrowLeft, ChevronDown, Check,
} from 'lucide-react';
import {
  adminGetEmailLogs,
  adminGetEmailLogDetail,
  adminGetEmailLogStats,
  adminGetEmailTemplates,
} from '../../services/notificationService';
import { useNotification } from '../../notification/NotificationContext';
import './AdminEmailTemplatesPage.css';
import './AdminEmailLogsPage.css';

const PAGE_SIZE = 20;

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDateTimeFull = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZone: 'UTC', timeZoneName: 'short',
  });
};

const StatusBadge = ({ status }) => (
  <span className={`ael-pill ${status === 'Sent' ? 'ael-pill-on' : 'ael-pill-off'}`}>
    {status === 'Sent' ? '✓' : '✗'} {status}
  </span>
);

const SlugBadge = ({ slug }) => (
  <code className="aet-slug">{slug}</code>
);

const SearchableSelect = ({ value, options, placeholder, allLabel, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); setQ(''); }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(needle) ||
          (o.value || '').toLowerCase().includes(needle),
      )
    : options;

  const choose = (val) => {
    onChange(val);
    setOpen(false);
    setQ('');
  };

  return (
    <div className="ael-ssel" ref={wrapRef}>
      <button
        type="button"
        className="aet-input ael-select ael-ssel-btn"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected ? 'ael-ssel-label' : 'ael-ssel-placeholder'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} className={`ael-ssel-caret ${open ? 'ael-ssel-caret-open' : ''}`} />
      </button>
      {open && (
        <div className="ael-ssel-pop">
          <div className="ael-ssel-search">
            <Search size={13} className="ael-ssel-search-ic" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search templates..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="ael-ssel-list">
            <button
              type="button"
              className={`ael-ssel-opt ${!value ? 'ael-ssel-opt-active' : ''}`}
              onClick={() => choose('')}
            >
              <span>{allLabel}</span>
              {!value && <Check size={14} />}
            </button>
            {filtered.length === 0 ? (
              <div className="ael-ssel-empty">No templates match.</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`ael-ssel-opt ${o.value === value ? 'ael-ssel-opt-active' : ''}`}
                  onClick={() => choose(o.value)}
                >
                  <span>{o.label}</span>
                  {o.value === value && <Check size={14} />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tone, onClick }) => (
  <button
    type="button"
    className={`ael-stat ael-stat-${tone} ${onClick ? 'ael-stat-clickable' : ''}`}
    onClick={onClick}
    disabled={!onClick}
  >
    <span className="ael-stat-icon"><Icon size={20} /></span>
    <span className="ael-stat-body">
      <span className="ael-stat-label">{label}</span>
      <span className="ael-stat-value">{(value ?? 0).toLocaleString()}</span>
    </span>
  </button>
);

const AdminEmailLogsPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [slugFilter, setSlugFilter] = useState('');
  const [templates, setTemplates] = useState([]);

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const firstLoadRef = useRef(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, slugFilter]);

  const loadStats = useCallback(async () => {
    try {
      const data = await adminGetEmailLogStats();
      setStats(data);
    } catch (err) {
      showNotification(err.message || 'Failed to load stats', 'error');
    }
  }, [showNotification]);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await adminGetEmailTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      // silent — template dropdown is optional
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminGetEmailLogs({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter || undefined,
        slug: slugFilter || undefined,
        search: debouncedSearch || undefined,
      });
      setLogs(data?.items || []);
      setTotalCount(data?.totalCount || 0);
      setTotalPages(data?.totalPages || 1);
    } catch (err) {
      showNotification(err.message || 'Failed to load email logs', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, slugFilter, debouncedSearch, showNotification]);

  useEffect(() => { loadStats(); loadTemplates(); }, [loadStats, loadTemplates]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleRefresh = () => { loadStats(); loadLogs(); };

  const handleClearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setSlugFilter('');
  };

  const openDetail = async (id) => {
    setDrawerOpen(true);
    setSelected(null);
    setDetailLoading(true);
    try {
      const data = await adminGetEmailLogDetail(id);
      setSelected(data);
    } catch (err) {
      showNotification(err.message || 'Failed to load detail', 'error');
      setDrawerOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
  };

  const openFullHtml = () => {
    if (!selected?.renderedBody) return;
    const blob = new Blob([selected.renderedBody], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const pageNumbers = (() => {
    const nums = [];
    const max = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + max - 1);
    start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) nums.push(i);
    return nums;
  })();

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <div className="adm-shell">
      <header className="adm-header">
        <button className="adm-logo" onClick={() => navigate('/admin/dashboard')} type="button">
          <ArrowLeft size={16} />
          <span>Back to Admin</span>
        </button>
        <div className="adm-header-right">
          <span className="adm-header-badge">Email Logs</span>
          <button className="adm-logout" onClick={handleLogout}>Logout →</button>
        </div>
      </header>

      <main className="aet-main">
        <div className="aet-toolbar">
          <div>
            <h1 className="aet-title">Email Logs</h1>
            <p className="aet-sub">Every email send attempt — succeeded or failed.</p>
          </div>
          <div className="aet-actions">
            <button className="aet-btn aet-btn-ghost" onClick={handleRefresh} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        <section className="ael-stats">
          <StatCard icon={MailCheck} label="Total Sent" value={stats?.sent} tone="green" />
          <StatCard
            icon={AlertCircle}
            label="Failed"
            value={stats?.failed}
            tone="red"
            onClick={() => setStatusFilter('Failed')}
          />
          <StatCard icon={Calendar} label="Today" value={stats?.todayTotal} tone="blue" />
          <StatCard icon={Inbox} label="All Time" value={stats?.total} tone="gray" />
        </section>

        <section className="ael-filters">
          <div className="ael-search">
            <Search size={14} className="ael-search-ic" />
            <input
              type="text"
              className="aet-input"
              placeholder="Search email, subject, or slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="aet-input ael-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Sent">Sent</option>
            <option value="Failed">Failed</option>
          </select>
          <SearchableSelect
            value={slugFilter}
            placeholder="All Templates"
            allLabel="All Templates"
            onChange={setSlugFilter}
            options={templates.map((t) => ({
              value: t.slug,
              label: t.name || t.slug,
            }))}
          />
          {(search || statusFilter || slugFilter) && (
            <button className="aet-btn aet-btn-ghost" onClick={handleClearFilters} type="button">
              Clear
            </button>
          )}
        </section>

        <section className="aet-table-wrap">
          {loading ? (
            <div className="aet-loading">Loading email logs...</div>
          ) : logs.length === 0 ? (
            <div className="aet-empty">No email logs match your filters.</div>
          ) : (
            <table className="aet-table">
              <thead>
                <tr>
                  <th style={{ width: 56 }}>#</th>
                  <th>To Email</th>
                  <th>Subject</th>
                  <th>Template</th>
                  <th>Status</th>
                  <th>Sent At</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <tr key={log.id}>
                    <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                    <td className="ael-email">{log.toEmail}</td>
                    <td className="aet-subj">{log.subject}</td>
                    <td><SlugBadge slug={log.templateSlug} /></td>
                    <td><StatusBadge status={log.status} /></td>
                    <td className="ael-sent">{formatDateTime(log.sentAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="aet-icon-btn"
                        onClick={() => openDetail(log.id)}
                        title="View detail"
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {totalCount > 0 && (
          <section className="ael-pagination">
            <span className="ael-page-info">
              Showing {startIdx}–{endIdx} of {totalCount.toLocaleString()}
            </span>
            <div className="ael-page-controls">
              <button
                className="ael-page-btn"
                disabled={page === 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`ael-page-btn ${n === page ? 'ael-page-btn-active' : ''}`}
                  onClick={() => setPage(n)}
                  disabled={loading}
                >
                  {n}
                </button>
              ))}
              <button
                className="ael-page-btn"
                disabled={page === totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </section>
        )}
      </main>

      {drawerOpen && (
        <>
          <div className="aet-backdrop" onClick={closeDrawer} />
          <aside className="aet-drawer ael-drawer">
            <header className="aet-drawer-head">
              <div>
                <span className="aet-drawer-kicker">Email Log Detail</span>
                <h2>{selected?.subject || (detailLoading ? 'Loading...' : 'Email')}</h2>
              </div>
              <button className="aet-icon-btn" onClick={closeDrawer} aria-label="Close">
                <X size={16} />
              </button>
            </header>

            <div className="aet-drawer-body ael-drawer-body">
              {detailLoading || !selected ? (
                <div className="aet-loading">Loading email details...</div>
              ) : (
                <>
                  <div className="ael-info-card">
                    <div className="ael-info-row">
                      <span className="ael-info-key">To</span>
                      <span className="ael-info-val">{selected.toEmail}</span>
                    </div>
                    <div className="ael-info-row">
                      <span className="ael-info-key">Subject</span>
                      <span className="ael-info-val">{selected.subject}</span>
                    </div>
                    <div className="ael-info-row">
                      <span className="ael-info-key">Template</span>
                      <span className="ael-info-val"><SlugBadge slug={selected.templateSlug} /></span>
                    </div>
                    <div className="ael-info-row">
                      <span className="ael-info-key">Status</span>
                      <span className="ael-info-val"><StatusBadge status={selected.status} /></span>
                    </div>
                    <div className="ael-info-row">
                      <span className="ael-info-key">Sent At</span>
                      <span className="ael-info-val">{formatDateTimeFull(selected.sentAt)}</span>
                    </div>
                  </div>

                  {selected.status === 'Failed' && selected.errorMessage && (
                    <div className="ael-error-card">
                      <span className="ael-error-key">Error</span>
                      <code className="ael-error-msg">{selected.errorMessage}</code>
                    </div>
                  )}

                  <div className="ael-preview-head">
                    <span className="ael-preview-title">Email Preview</span>
                    {selected.renderedBody && (
                      <button className="aet-btn aet-btn-ghost" onClick={openFullHtml}>
                        <ExternalLink size={13} /> Open Full
                      </button>
                    )}
                  </div>

                  {selected.renderedBody ? (
                    <div className="ael-iframe-wrap">
                      <iframe
                        title="email-preview"
                        srcDoc={selected.renderedBody}
                        sandbox="allow-same-origin"
                        className="ael-iframe"
                      />
                    </div>
                  ) : (
                    <div className="aet-warn">
                      No email preview available for this log entry.
                    </div>
                  )}
                </>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default AdminEmailLogsPage;
